import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { updatePassword } from "@/lib/auth";
import { PasswordInput } from "@/components/auth/AuthShell";
import {
  AccountShell,
  SectionCard,
  Alert,
  GoldButton,
  inputClass,
  labelClass,
} from "@/components/account/AccountShell";

const schema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirm: z.string().min(8, "Please confirm your new password"),
  })
  .refine((d) => d.password === d.confirm, {
    path: ["confirm"],
    message: "Passwords do not match",
  });

export const Route = createFileRoute("/account/password")({
  component: PasswordPage,
});

function PasswordPage() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<{ password?: string; confirm?: string; form?: string }>({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    const parsed = schema.safeParse({ password, confirm });
    if (!parsed.success) {
      const fe = parsed.error.flatten().fieldErrors;
      setErrors({ password: fe.password?.[0], confirm: fe.confirm?.[0] });
      return;
    }
    setErrors({});
    setLoading(true);
    const { error } = await updatePassword(parsed.data.password);
    setLoading(false);
    if (error) {
      setErrors({ form: error });
      return;
    }
    setPassword("");
    setConfirm("");
    setSaved(true);
  }

  return (
    <AccountShell>
      <SectionCard title="Change Password">
        {saved && (
          <div className="mb-6">
            <Alert tone="success">Your password has been updated.</Alert>
          </div>
        )}
        {errors.form && (
          <div className="mb-6">
            <Alert tone="error">{errors.form}</Alert>
          </div>
        )}
        <form onSubmit={onSubmit} className="grid max-w-md gap-5">
          <div>
            <label htmlFor="password" className={labelClass}>
              New password
            </label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
          </div>
          <div>
            <label htmlFor="confirm" className={labelClass}>
              Confirm new password
            </label>
            <PasswordInput
              id="confirm"
              autoComplete="new-password"
              className={inputClass}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {errors.confirm && <p className="mt-1 text-xs text-red-600">{errors.confirm}</p>}
          </div>
          <div>
            <GoldButton type="submit" disabled={loading}>
              {loading ? "Updating…" : "Update password"}
            </GoldButton>
          </div>
        </form>
      </SectionCard>
    </AccountShell>
  );
}
